import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
} from '@mui/material';
import { FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInput, DialogTitleWithClose, LinkCardOption, StatusCode } from '../../../shared';
import { useCreateCard, useLinkCardSupportCase } from '../../hooks';
import { useCardSchema } from '../../validators/card.validator';

interface Props {
    open: boolean;
    supportCase: string;
    handleClose: () => void;
}

export const LinkCardModal: FC<Props> = ({ open, supportCase, handleClose }) => {
    const { t } = useTranslation();

    const form = useForm({ resolver: yupResolver(useCardSchema()) });
    const watchAction = form.watch('action');

    useEffect(() => {
        const option = window.carity.environment.defaultLinkCardOption;
        const defaultOption = Object.values(LinkCardOption).includes(option as LinkCardOption) ? option : '';
        form.setValue('action', defaultOption);
    }, [open, form]);

    const { mutateAsync: createCard } = useCreateCard();
    const { mutateAsync: linkCardToSupportCase } = useLinkCardSupportCase();

    const onSubmit = async (data: any /*{ action: string; code: string }*/) => {
        try {
            if (data.action == LinkCardOption.NEW_CARD) {
                await createCard(supportCase);
            } else {
                await linkCardToSupportCase({ supportCase: supportCase, cardCode: data.code });
            }
            handleClose();
        } catch (e: any) {
            if (e.response.status === StatusCode.NOT_FOUND) {
                form.setError('code', { message: t('codeNotFoundError') });
            } else if (e.response.status === StatusCode.CONFLICT) {
                form.setError('code', { message: t('codeConflictError') });
            }
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate={true}>
                    <DialogTitleWithClose onClose={handleClose}>{t('linkCard')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Controller
                                name="action"
                                defaultValue={LinkCardOption.EXISTING_CARD}
                                render={({ field: { onChange, value } }) => (
                                    <RadioGroup value={value} onChange={onChange} sx={{ '.MuiRadio-root': { pt: 0 } }}>
                                        <FormControlLabel
                                            value={LinkCardOption.EXISTING_CARD}
                                            control={<Radio />}
                                            label={`${t('existingCard')}`}
                                            data-testid="radio-existing"
                                        />
                                        <FormControlLabel
                                            value={LinkCardOption.NEW_CARD}
                                            control={<Radio />}
                                            label={`${t('newCard')}`}
                                            data-testid="radio-new"
                                        />
                                    </RadioGroup>
                                )}
                            />

                            {watchAction === LinkCardOption.EXISTING_CARD && (
                                <ControlledInput name="code" label={`${t('code')}`} required autoFocus />
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" data-testid="confirm" disabled={watchAction === ''}>
                            {t('linkCard')}
                        </Button>
                        <Button onClick={handleClose} color="primary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
